import { Controller } from "@hotwired/stimulus"
import { normaliseConditions } from "../schema_conditions.js"
import { formatBound, parseNumber } from "../schema_number.js"

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/
const RANGE_UNIT = { money_pln: "zł", area_m2: "m²" } // the facet's `unit` is blank for these types

// Max offsetTop difference (px) between items of one bar row (centred items of different heights).
const ROW_TOLERANCE = 12

/*
  ll-filters — pasek filtrów C10–C13.
  Umieszczony na wrapperze #ll-filters-scope (obejmuje wyzwalacz C31, tło arkusza i <form id="ll-filters">);
  nadal działa też na samym <form> (wtedy bez wyzwalacza / tła).

  Zachowania:
  - Radio / checkbox / switch (change action na form) → requestSubmit().
  - window combobox:change z detail.scope === this form, detail.silent !== true
    → requestSubmit() (deduplikacja: zmiana województwa powoduje też wyczyszczenie
    powiatu synchronicznie, więc dwa eventy muszą dać jeden submit).
  - .ll-dd (details): zmiana checkboxa wewnątrz = aktualizacja licznika .ll-dd-n
    i klasy .has-value bez submitu; zamknięcie details → requestSubmit().
  - Klik poza otwartym .ll-dd / Esc → zamknięcie .ll-dd.
  - ResizeObserver (debounce 100 ms): przepływ akordeonowy — gdy dynamiczne filtry
    z #ll-f-dyn nie mieszczą się w jednym wierszu paska, ostatni przenoszony jest
    na START #ll-f-adv; toggle advToggle pokazywany gdy panel ma elementy;
    advCount = liczba aktywnych filtrów w panelu; panel otwierany gdy aktywny filtr.
  - toggleAdvanced: przełącza hidden na advPanel + aria-expanded na advToggle.
  - Arkusz filtrów C31 (≤760 px): open()/close() przełączają is-open na elemencie kontrolera, hidden
    na tle, aria-expanded na wyzwalaczu, blokadę przewijania body (klasa ll-scroll-lock); Esc zamyka;
    fokus → arkusz przy otwarciu, → wyzwalacz przy zamknięciu. Przy otwartym arkuszu auto-submit jest
    wyłączony (formularz wysyła „Pokaż wyniki"). Target count = liczba aktywnych filtrów.
  - Reflow nie działa w arkuszu (≤760 px): wszystko inline, toggle „Zaawansowane" ukryty.
    ≤1240 px cel reflow = „mieści się w dwóch wierszach"; ≥1241 px — w jednym. Przycisk „Wyczyść filtry"
    i widoczny toggle „Zaawansowane" liczą się jako elementy paska. Gdy po przeniesieniu wszystkich filtrów
    dynamicznych pasek nadal nie mieści się w jednym wierszu (≥1241 px), filtry wracają do paska, a formularz
    dostaje klasę is-two-rows (układ dwuwierszowy jak ≤1240 px) — sortowanie nigdy nie ląduje samo w wierszu.
  - disconnect: usuwa window / document listeners, rozłącza ResizeObserver, zwalnia blokadę body.
*/
export default class extends Controller {
  static targets = ["advToggle", "advCount", "advPanel", "trigger", "count", "backdrop", "sheet"]

  connect() {
    this._form = this.element.matches("form")
      ? this.element
      : this.element.querySelector("form")
    if (!this._form) return

    // Capture original order of #ll-f-dyn children for overflow restore.
    this._dynContainer = this._form.querySelector("#ll-f-dyn")
    this._dynOrder = this._dynContainer ? Array.from(this._dynContainer.children) : []

    this._submitPending = false
    this._open = false

    // Viewport queries: ≤760 = bottom sheet (no reflow), ≤1240 = two-row bar. jsdom has no matchMedia.
    this._mqlMobile = this._mql("(max-width: 760px)")
    this._mqlTwoRows = this._mql("(max-width: 1240px)")
    this._onMqlChange = () => {
      if (this._open && !this._isMobile()) this.close({ restoreFocus: false })
      this._reflow()
    }
    this._mqlMobile?.addEventListener?.("change", this._onMqlChange)
    this._mqlTwoRows?.addEventListener?.("change", this._onMqlChange)

    // window combobox:change — submit once per interaction (deduplication)
    this._onComboboxChange = (e) => {
      if (e.detail.scope !== this._form) return
      this._syncCount()
      if (e.detail.silent) return
      this._scheduleSubmit()
    }
    window.addEventListener("combobox:change", this._onComboboxChange)

    // .ll-dd toggle (capture phase so the event fires after details changes state)
    this._onDdToggle = (e) => {
      if (!e.target.classList?.contains("ll-dd")) return
      // The „Kategoria” dropdown holds links (the selection is the URL): no counter, no submit.
      if (e.target.classList.contains("ll-dd--links")) return
      this._syncDdCounter(e.target)
      if (!e.target.open) this._scheduleSubmit()
    }
    this._form.addEventListener("toggle", this._onDdToggle, true)

    // Outside click closes open .ll-dd
    this._onDocClick = (e) => {
      this._form.querySelectorAll("details.ll-dd[open]").forEach(dd => {
        if (!dd.contains(e.target)) dd.open = false
      })
    }
    document.addEventListener("click", this._onDocClick)

    // Esc closes open .ll-dd
    this._onKeydown = (e) => {
      if (e.key !== "Escape") return
      const openDds = this._form.querySelectorAll("details.ll-dd[open]")
      if (openDds.length) {
        openDds.forEach(dd => { dd.open = false })
      } else if (this._open) {
        this.close()
      }
    }
    document.addEventListener("keydown", this._onKeydown)

    // ResizeObserver: reflow on container resize (debounced 100 ms)
    if (typeof ResizeObserver !== "undefined") {
      this._ro = new ResizeObserver(() => {
        clearTimeout(this._roTimer)
        this._roTimer = setTimeout(() => this._reflow(), 100)
      })
      this._ro.observe(this._form)
    }
    // Web fonts change control widths after connect (the reset button is server-rendered on reload).
    document.fonts?.ready?.then?.(() => { if (this._form?.isConnected) this._reflow() })
    // Schema filters (sf_*): the server renders them unchecked/empty, the URL is the state.
    this._restoreSchemaState()
    const staleCleared = this._syncSchemaVisibility({ reflow: false })
    this._ensureReset()
    this._reflow()
    this._syncCount()
    if (staleCleared) this._scheduleSubmit() // the URL held a type-specific filter that does not apply: canonicalise it
  }

  disconnect() {
    this._unlockScroll()
    this._mqlMobile?.removeEventListener?.("change", this._onMqlChange)
    this._mqlTwoRows?.removeEventListener?.("change", this._onMqlChange)
    if (this._onComboboxChange) {
      window.removeEventListener("combobox:change", this._onComboboxChange)
      this._onComboboxChange = null
    }
    if (this._onDdToggle) {
      this._form?.removeEventListener("toggle", this._onDdToggle, true)
      this._onDdToggle = null
    }
    if (this._onDocClick) {
      document.removeEventListener("click", this._onDocClick)
      this._onDocClick = null
    }
    if (this._onKeydown) {
      document.removeEventListener("keydown", this._onKeydown)
      this._onKeydown = null
    }
    this._ro?.disconnect()
    clearTimeout(this._roTimer)
  }

  // Action: fired by data-action="change->ll-filters#change" on <form>.
  // Radios/checkboxes inside .ll-dd update the counter; submit fires on details close.
  // Other radios/checkboxes/switches → schedule submit immediately.
  change(event) {
    const target = event.target
    if (target?.matches?.('.ls-combo__input') || (target?.matches?.('input:not([name])') && target.closest('.ls-combo'))) return
    // Range „od … do …" (schema filters): normalise into the hidden sf_*__min/__max inputs; garbage is ignored silently.
    const rangeInput = event.target?.closest?.(".ll-range-input")
    if (rangeInput) {
      const result = this._applyRange(rangeInput.closest("[data-ll-schema-filter]"))
      this._syncCount()
      if (result === "changed") this._scheduleSubmit()
      return
    }
    this._syncCount()
    this._syncSchemaVisibility()
    const inDd = event.target?.closest?.(".ll-dd")
    if (inDd) {
      this._syncDdCounter(inDd)
      return
    }
    this._scheduleSubmit()
  }

  // Action: open the filter bottom sheet (C31).
  open() {
    if (this._open) return
    this._open = true
    this.element.classList.add("is-open")
    if (this.hasBackdropTarget) this.backdropTarget.hidden = false
    if (this.hasTriggerTarget) this.triggerTarget.setAttribute("aria-expanded", "true")
    document.body.classList.add("ll-scroll-lock")
    this._sheetEl()?.focus?.()
  }

  // Action: close the sheet (backdrop click, Esc, viewport leaves ≤760 px). Focus returns to the trigger.
  close({ restoreFocus = true } = {}) {
    if (!this._open) return
    this._open = false
    this.element.classList.remove("is-open")
    if (this.hasBackdropTarget) this.backdropTarget.hidden = true
    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", "false")
      if (restoreFocus === true) this.triggerTarget.focus?.()
    }
    this._unlockScroll()
  }

  // Action: toggle advanced panel open/closed.
  toggleAdvanced() {
    if (!this.hasAdvPanelTarget) return
    const panel = this.advPanelTarget
    const toggle = this.hasAdvToggleTarget ? this.advToggleTarget : null
    const willOpen = panel.hidden
    panel.hidden = !willOpen
    if (toggle) toggle.setAttribute("aria-expanded", String(willOpen))
  }

  // --- private ---

  _mql(query) {
    return typeof window.matchMedia === "function" ? window.matchMedia(query) : null
  }

  _isMobile() { return !!this._mqlMobile?.matches }

  _sheetEl() {
    return this.hasSheetTarget ? this.sheetTarget : this._form
  }

  _unlockScroll() {
    document.body.classList.remove("ll-scroll-lock")
  }

  // Number of active filters — the same notion as the reset button: voivodeship (+1), every county (+1),
  // favourites (+1), one per dynamic f_<key> with a non-default value, one for a selected category.
  // Sort/direction are not filters. Badge hidden at 0.
  _activeCount() {
    if (!this._form) return 0
    let n = 0
    const dynKeys = new Set()
    for (const el of this._form.elements) {
      const name = el.name
      if (!name) continue
      if (name === "voivodeship" || name === "county") {
        if (el.type === "hidden" && el.value !== "") n++
      } else if (name === "favorites") {
        if (el.checked) n++
      } else if (name.startsWith("f_")) {
        if ((el.type === "radio" || el.type === "checkbox") && el.checked && el.value !== "") dynKeys.add(name)
      } else if (name.startsWith("sf_") && !el.disabled) {
        // Schema filters: one per param (radios/checkboxes) or per range (its min/max hidden inputs).
        if ((el.type === "radio" || el.type === "checkbox") && el.checked && el.value !== "") dynKeys.add(name)
        else if (el.type === "hidden" && el.value !== "") dynKeys.add(name.replace(/__(min|max)$/, ""))
      }
    }
    n += dynKeys.size
    const cat = this._form.querySelector("input[type='hidden'][name='category'], input[type='hidden'][name='subcategory']")
    if (cat && cat.value !== "") n++
    return n
  }

  _syncCount() {
    if (!this.hasCountTarget) return
    const n = this._activeCount()
    this.countTarget.textContent = String(n)
    this.countTarget.hidden = n === 0
  }

  _scheduleSubmit() {
    // While the sheet is open the form submits only via „Pokaż wyniki".
    if (this._open) return
    if (this._submitPending) return
    this._submitPending = true
    setTimeout(() => {
      this._submitPending = false
      this._form?.requestSubmit?.()
    }, 0)
  }

  _syncDdCounter(dd) {
    const count = dd.querySelectorAll("input:checked").length
    dd.classList.toggle("has-value", count > 0)
    const badge = dd.querySelector(".ll-dd-n")
    if (badge) {
      badge.textContent = String(count)
      badge.hidden = count === 0
    }
  }

  // Overflow reflow:
  // 1. Restore all original dyn items to #ll-f-dyn in original order.
  // 2. ≤760 px (bottom sheet): stop — every control stays inline, the advanced toggle is hidden.
  // 3. While the bar items occupy more rows than allowed (1 row ≥1241 px, 2 rows ≤1240 px) →
  //    move the LAST #ll-f-dyn child to START of advPanel.
  // 4. Update toggle visibility, advCount, and auto-open panel when active.
  _reflow() {
    if (!this._dynContainer || !this.hasAdvPanelTarget) return
    const advPanel = this.advPanelTarget

    // Step 1: restore — detach every original item and re-append to dynContainer in order.
    this._restoreDyn()
    this._form.classList.remove("is-two-rows")

    // Step 2: the sheet shows everything inline.
    if (this._isMobile()) {
      if (this.hasAdvToggleTarget) {
        this.advToggleTarget.hidden = true
        this.advToggleTarget.setAttribute("aria-expanded", "false")
      }
      if (this.hasAdvCountTarget) {
        this.advCountTarget.textContent = "0"
        this.advCountTarget.hidden = true
      }
      advPanel.hidden = true
      return
    }

    // Step 3: detect and resolve overflow by moving items from dynContainer to advPanel.
    // ≥1241 px the target is one row. When even built-ins + reset + the „Zaawansowane" toggle + sort cannot
    // share one row, moving more filters out cannot help (sort would sit alone on a second row): fall back
    // to the two-row layout (.is-two-rows, same rules as ≤1240 px) with the dynamic filters back in the bar.
    const maxRows = this._mqlTwoRows?.matches ? 2 : 1
    if (!this._fitRows(maxRows) && maxRows === 1) {
      this._restoreDyn()
      this._form.classList.add("is-two-rows")
      this._fitRows(2)
    }

    // Step 4: toggle visibility and count.
    const hasItems = advPanel.children.length > 0
    if (this.hasAdvToggleTarget) this.advToggleTarget.hidden = !hasItems

    if (hasItems) {
      const activeCount = this._countActiveInPanel(advPanel)
      if (this.hasAdvCountTarget) {
        this.advCountTarget.textContent = String(activeCount)
        this.advCountTarget.hidden = activeCount === 0
      }
      // Open the panel immediately when it holds an active filter.
      if (activeCount > 0) {
        advPanel.hidden = false
        if (this.hasAdvToggleTarget) this.advToggleTarget.setAttribute("aria-expanded", "true")
      }
    } else {
      if (this.hasAdvCountTarget) {
        this.advCountTarget.textContent = "0"
        this.advCountTarget.hidden = true
      }
    }
  }

  // Put every original dynamic item back into #ll-f-dyn (original order); the advanced toggle is hidden again.
  _restoreDyn() {
    for (const item of this._dynOrder) {
      item.parentNode?.removeChild(item)
      this._dynContainer.appendChild(item)
    }
    if (this.hasAdvToggleTarget) this.advToggleTarget.hidden = true
  }

  // Moves the LAST dynamic item to the start of the advanced panel until the bar occupies at most maxRows rows.
  // The „Zaawansowane" toggle takes space in the bar as soon as the panel holds an item, so it is shown while
  // measuring. Returns false when the bar still overflows with every dynamic item moved out.
  _fitRows(maxRows) {
    const advPanel = this.advPanelTarget
    for (;;) {
      if (advPanel.children.length > 0 && this.hasAdvToggleTarget) this.advToggleTarget.hidden = false
      const items = this._getBarItems()
      if (items.length === 0 || this._rowCount(items) <= maxRows) return true
      if (this._dynContainer.children.length === 0) return false
      advPanel.insertBefore(this._dynContainer.lastElementChild, advPanel.firstChild)
    }
  }

  // Visual rows of the bar. Items are vertically centred, so a shorter one in the same row has a slightly larger
  // offsetTop (browser 2026-10-02: the reset link sat 3 px lower and counted as a second row) — tops within
  // ROW_TOLERANCE of a row's first item belong to that row.
  _rowCount(items) {
    const tops = items.map(el => el.offsetTop).sort((a, b) => a - b)
    let rows = 0
    let rowStart = Number.NEGATIVE_INFINITY
    for (const top of tops) {
      if (top - rowStart > ROW_TOLERANCE) {
        rows++
        rowStart = top
      }
    }
    return rows
  }

  // Bar items: direct children of the form that participate in the flex bar rows.
  // Excludes the advanced panel (the toggle counts while it is visible: it occupies space in the row),
  // mobile-only (.ll-only-m) / tablet-only (.ll-only-t) elements, hidden elements, and the hidden inputs.
  // #ll-f-dyn is `display: contents` in the real page: it has no box and reports offsetTop 0, which the row count
  // read as a second row (browser 2026-10-02 at 1440: the bar fell back to two rows although it fitted one), so a
  // boxless container is replaced by its children. A container with a box (tests, jsdom) is measured as itself.
  _getBarItems() {
    const children = Array.from(this._form.children).flatMap(el =>
      el === this._dynContainer && getComputedStyle(el).display === "contents" ? Array.from(el.children) : [el],
    )
    return children.filter(el => {
      if (this.hasAdvPanelTarget && el === this.advPanelTarget) return false
      if (el.classList.contains("ll-only-m")) return false
      // „Kategoria" (≤1180 px) is always the first item and never wraps — it must not skew the row count.
      if (el.classList.contains("ll-only-t")) return false
      if (el.hidden) return false
      if (el.tagName === "INPUT") return false
      return true
    })
  }

  // Count active (checked, non-empty value) inputs in a container.
  _countActiveInPanel(panel) {
    let n = 0
    panel.querySelectorAll("input[type='radio']:checked").forEach(inp => {
      if (inp.value !== "") n++
    })
    panel.querySelectorAll("input[type='checkbox']:checked").forEach(() => { n++ })
    panel.querySelectorAll(".ll-range").forEach(range => {
      if (range.querySelector("input[type='hidden']:not(:disabled)")) n++
    })
    return n
  }

  // ---- schema filters (sf_*) ------------------------------------------------

  _schemaGroups() {
    return Array.from(this._form.querySelectorAll("[data-ll-schema-filter]"))
  }

  // Restore checked options / range values from the URL (the server-rendered markup carries no state for sf_*).
  _restoreSchemaState() {
    const params = new URLSearchParams(window.location.search)
    for (const group of this._schemaGroups()) {
      const param = group.dataset.param
      if (group.classList.contains("ll-range")) {
        this._restoreRange(group, params)
        continue
      }
      const wanted = params.getAll(param)
      const inputs = Array.from(group.querySelectorAll(`input[name="${param}"]`))
      let any = false
      for (const input of inputs) {
        input.checked = input.value !== "" && wanted.includes(input.value)
        any = any || input.checked
      }
      const every = inputs.find(i => i.value === "")
      if (every) every.checked = !any
      if (group.matches("details.ll-dd")) this._syncDdCounter(group)
    }
  }

  _restoreRange(group, params) {
    const isDate = group.dataset.filterType === "date"
    for (const bound of ["min", "max"]) {
      const visible = group.querySelector(`[data-range-bound="${bound}"]`)
      const hidden = group.querySelector(`input[type="hidden"][name$="__${bound}"]`)
      const unit = group.querySelector(".ll-range-unit")
      if (unit && !unit.textContent.trim()) unit.textContent = RANGE_UNIT[group.dataset.filterType] ?? ""
      const limit = group.dataset[bound]
      if (limit && !isDate) visible.placeholder = `${bound === "min" ? "od" : "do"} ${formatBound(limit)}`
      else if (!isDate) visible.placeholder = bound === "min" ? "od" : "do"
      const raw = params.get(`${group.dataset.param}__${bound}`) ?? ""
      const valid = isDate ? ISO_DATE.test(raw) : raw !== "" && parseNumber(raw, this._numberKind(group)).ok
      hidden.value = valid ? raw : ""
      hidden.disabled = !valid
      visible.value = valid ? (isDate ? raw : raw.replace(".", ",")) : ""
    }
  }

  _numberKind(group) {
    return group.dataset.filterType === "integer" ? "integer" : "decimal"
  }

  // Normalise a range's visible inputs into its hidden params. → "changed" | "same" | "invalid".
  // Invalid text is left in the box (red hint) and nothing is sent: no request, no message.
  _applyRange(group) {
    if (!group) return "same"
    const isDate = group.dataset.filterType === "date"
    let result = "same"
    for (const bound of ["min", "max"]) {
      const visible = group.querySelector(`[data-range-bound="${bound}"]`)
      const hidden = group.querySelector(`input[type="hidden"][name$="__${bound}"]`)
      const raw = visible.value.trim()
      let value = ""
      if (raw !== "") {
        if (isDate) {
          if (!ISO_DATE.test(raw)) { visible.classList.add("is-invalid"); return "invalid" }
          value = raw
        } else {
          const parsed = parseNumber(raw, this._numberKind(group))
          if (!parsed.ok) { visible.classList.add("is-invalid"); return "invalid" }
          value = String(parsed.value)
        }
      }
      visible.classList.remove("is-invalid")
      if (hidden.value !== value) result = "changed"
      hidden.value = value
      hidden.disabled = value === ""
    }
    return result
  }

  // Action: Enter in a range box applies it instead of the native submit (which would miss the hidden values).
  rangeKey(event) {
    if (event.key !== "Enter") return
    event.preventDefault()
    this.change({ target: event.target })
  }

  _selectedOf(group) {
    return Array.from(group.querySelectorAll("input[type='radio']:checked, input[type='checkbox']:checked"))
      .map(i => i.value).filter(v => v !== "")
  }

  _conditionsOf(group) {
    const script = group.querySelector("script[type='application/json']")
    if (!script) return []
    try {
      return normaliseConditions(JSON.parse(script.textContent))
    } catch {
      return []
    }
  }

  // Type-specific visibility: a schema filter with `visible_when` is shown only while EVERY condition holds —
  // the controlling filter (schema or f_*) has a selected value that is in the condition's `in` list. A hidden
  // controller counts as having no selection (transitive); no selection hides the dependants. A hidden filter is
  // cleared and disabled, so its params leave the URL. → true when a hidden filter had a value that was dropped.
  _syncSchemaVisibility({ reflow = true } = {}) {
    const groups = this._schemaGroups()
    const byKey = new Map(groups.map(g => [g.dataset.filterKey, g]))
    const memo = new Map()
    const inProgress = new Set()

    const selected = (key) => {
      const controller = byKey.get(key)
      if (controller) return visible(controller) ? this._selectedOf(controller) : []
      return Array.from(this._form.querySelectorAll(`input[name="f_${key}"]:checked`)).map(i => i.value).filter(Boolean)
    }
    const visible = (group) => {
      if (memo.has(group)) return memo.get(group)
      const conditions = this._conditionsOf(group)
      if (!conditions.length) { memo.set(group, true); return true }
      if (inProgress.has(group)) return false
      inProgress.add(group)
      const ok = conditions.every(c => {
        const allowed = (Array.isArray(c.in) ? c.in : []).map(String)
        return selected(c.key).some(v => allowed.includes(v))
      })
      inProgress.delete(group)
      memo.set(group, ok)
      return ok
    }

    let changed = false
    let cleared = false
    const show = new Map(groups.map(g => [g, visible(g)]))
    for (const group of groups) {
      const shown = show.get(group)
      if (group.hidden === !shown) continue
      changed = true
      group.hidden = !shown
      if (!shown && this._clearGroup(group)) cleared = true
      this._setGroupDisabled(group, !shown)
    }
    if (changed) {
      this._syncCount()
      if (reflow) this._reflow()
    }
    return cleared
  }

  // Reset a schema filter to „no selection". → true when it held a value.
  _clearGroup(group) {
    let had = false
    for (const input of group.querySelectorAll("input")) {
      if (input.type === "radio") {
        const wantChecked = input.value === ""
        if (input.checked && !wantChecked) had = true
        input.checked = wantChecked
      } else if (input.type === "checkbox") {
        if (input.checked) had = true
        input.checked = false
      } else if (input.type === "hidden") {
        if (input.value !== "") had = true
        input.value = ""
      } else if (input.classList.contains("ll-range-input")) {
        input.value = ""
        input.classList.remove("is-invalid")
      }
    }
    if (group.matches("details.ll-dd")) this._syncDdCounter(group)
    return had
  }

  _setGroupDisabled(group, disabled) {
    for (const input of group.querySelectorAll("input")) {
      input.disabled = disabled || (input.type === "hidden" && input.value === "")
    }
  }

  // The server shows „Wyczyść filtry" only for filters it counts (f_*); sf_* params are invisible to it.
  _ensureReset() {
    if (this._form.querySelector("#ll-f-reset") || this._activeCount() === 0) return
    const sheetReset = this._form.querySelector(".ll-sheet-head a")
    if (!sheetReset) return
    const link = document.createElement("a")
    link.id = "ll-f-reset"
    link.className = "ll-btn ll-btn-text"
    link.href = sheetReset.getAttribute("href")
    link.textContent = "Wyczyść filtry"
    link.setAttribute("data-tour", "lead-filters.reset")
    this._form.insertBefore(link, this._form.querySelector("#ll-f-adv-toggle"))
  }
}
